import { OtpManager } from './OtpManager';
import { TokenManager } from './TokenManager';
import type { loginParams } from '../model/authSDKModels';
import type { IAuthSDKService } from '../model/IAuthSDKService';
import { AuthSDKEvents } from './AuthSDKEvents';
import { useLocalStorage } from '@vueuse/core';
import { ELocalStorageKeys } from '~/features/store/ELocalStorageKeys';

export class AuthSDKController {
  private static instance: AuthSDKController | null = null;
  
  public static getInstance(api: IAuthSDKService): AuthSDKController {
    if (!AuthSDKController.instance) {
      AuthSDKController.instance = new AuthSDKController(api);
    }

    return AuthSDKController.instance;
  }

  public magnitIdCode = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__MAGNIT_ID_CODE, '', {
    listenToStorageChanges: false,
    writeDefaults: false,
  });

  private readonly api: IAuthSDKService;

  private readonly tokenManager: TokenManager;
  private readonly otpManager: OtpManager;

  constructor(api: IAuthSDKService) {
    this.api = api;
    this.tokenManager = new TokenManager(api);
    this.otpManager = new OtpManager(api);
  }

  public isAuthorized() {
    return this.tokenManager.isAuthorized();
  }

  public isValid() {
    return this.tokenManager.isValid();
  }

  public async getToken() {
    return await this.tokenManager.getToken();
  }

  // Api запросы
  public async sendOtp(phone: string, captcha: string) {
    await this.otpManager.sendOTP(phone, captcha);
  }

  public async verifyOtp(otp: string) {
    const response = await this.otpManager.verifyOtp(otp);
    this.magnitIdCode.value = response.magnitIDCode;

    if (response.isRegistered) {
      await this.login({
        magnitIDCode: response.magnitIDCode,
        userId: response.userId,
      });
    }

    return {
      isRegistered: response.isRegistered,
      magnitIDCode: response.magnitIDCode,
      userId: response.userId,
    }
  }

  public async login(params: loginParams) {
    const response = await this.api.login(params);

    this.tokenManager.setTokens(response.accessToken, response.refreshToken);
    this.otpManager.clearOtpData();

    AuthSDKEvents.auth.emit()
  }

  public async refreshTokens() {
    await this.tokenManager.refresh();
  }
  // ----------------------------

  public logout() {
    this.tokenManager.clearTokens();
    this.otpManager.clearOtpData();
    this.magnitIdCode.value = undefined;

    AuthSDKEvents.logout.emit()
  }

  public DEBUG_SET_EXPIRED_AT(exp: string) {
    this.tokenManager.DEBUG_SET_EXPIRED_AT(exp);
  }

  // Сохранение состояния OTP
  public get sendedOtpData() {
    return this.otpManager.sendedOtpData;
  }

  public getSendedOtpData() {
    return this.otpManager.sendedOtpData;
  }
}
